import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ReportPreviewData, ReportGenerationOptions, ReportType } from '../types';
import { HoldingWithInstrumentAndAccount, TransactionWithInstrumentAndAccount } from '@/types';
import logoSvg from '@/assets/daappa_logo.svg?raw';

interface GeneratePDFOptions {
  reportData: ReportPreviewData;
  reportOptions: ReportGenerationOptions;
  reportType: ReportType;
  holdings: HoldingWithInstrumentAndAccount[];
  transactions: TransactionWithInstrumentAndAccount[];
}

const svgToPngDataUrl = (svgText: string, maxWidthMm: number, maxHeightMm: number, scale: number = 3): Promise<{ dataUrl: string; width: number; height: number; }> => {
  return new Promise((resolve, reject) => {
    const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const PX_PER_MM = 96 / 25.4;
      const naturalWidthPx = img.naturalWidth || img.width;
      const naturalHeightPx = img.naturalHeight || img.height;
      const naturalWidthMm = naturalWidthPx / PX_PER_MM;
      const naturalHeightMm = naturalHeightPx / PX_PER_MM;
      const ratio = Math.min(maxWidthMm / naturalWidthMm, maxHeightMm / naturalHeightMm);
      const width = Math.max(12, naturalWidthMm * ratio);
      const height = Math.max(12, naturalHeightMm * ratio);
      const canvas = document.createElement('canvas');
      const renderWidth = Math.ceil(width * PX_PER_MM * scale);
      const renderHeight = Math.ceil(height * PX_PER_MM * scale);
      canvas.width = renderWidth;
      canvas.height = renderHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error('Canvas 2D context unavailable'));
        return;
      }
      ctx.imageSmoothingEnabled = true;
      if ('imageSmoothingQuality' in (ctx as any)) {
        (ctx as any).imageSmoothingQuality = 'high';
      }
      ctx.drawImage(img, 0, 0, renderWidth, renderHeight);
      const dataUrl = canvas.toDataURL('image/png');
      URL.revokeObjectURL(url);
      resolve({ dataUrl, width, height });
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
};

export const generatePDFReport = async ({
  reportData,
  reportOptions,
  reportType,
  holdings,
  transactions,
}: GeneratePDFOptions): Promise<Blob> => {
  const doc = new jsPDF({ orientation: 'landscape' });
  const topMargin = 20;
  const bottomMargin = 15;
  let currentY = topMargin;

  // Helper functions
  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const addNewPageIfNeeded = (requiredSpace: number) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (currentY + requiredSpace > pageHeight - bottomMargin) {
      doc.addPage();
      currentY = topMargin;
    }
  };

  // Optional Logo (top-right) using embedded SVG converted to PNG data URL (no CORS)
  try {
    const pageWidth = doc.internal.pageSize.getWidth();
    const { dataUrl, width, height } = await svgToPngDataUrl(logoSvg as unknown as string, 48, 16);
    const logoX = pageWidth - 30 - width;
    const logoY = 20;
    doc.addImage(dataUrl, 'PNG', logoX, logoY, width, height);
  } catch (_) {
    // ignore logo load errors
  }

  // Title and Header
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(`Oversight Summary Report`, 20, currentY);
  currentY += 10;

  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(`Fund: ${reportData.fundName}`, 20, currentY);
  currentY += 6;
  doc.text(`Fund Key: ${reportData.fundKey}`, 20, currentY);
  currentY += 6;
  doc.text(`Reporting Period: ${reportData.reportingPeriod}`, 20, currentY);
  currentY += 6;
  doc.text(`Run date: ${reportData.asOfDate}`, 20, currentY);
  currentY += 6;
  doc.text(`Report Format: ${reportOptions.format}`, 20, currentY);
  currentY += 15;

  // NAV Summary Section
  if (reportOptions.includeNAVSummary && reportData.navSummary) {
    addNewPageIfNeeded(60);
    
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('NAV Summary', 20, currentY);
    currentY += 10;

    // Calculate portfolio totals by currency
    const portfolioTotals = holdings.reduce((totals, holding) => {
      const currency = holding.currency || 'USD';
      if (!totals[currency]) {
        totals[currency] = 0;
      }
      totals[currency] += holding.baseAmount;
      return totals;
    }, {} as Record<string, number>);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);

    Object.entries(portfolioTotals).forEach(([currency, total]) => {
      doc.text(`Total NAV (${currency}): ${formatCurrency(total, currency)}`, 20, currentY);
      currentY += 6;
    });

    doc.text(`Total Holdings: ${formatNumber(holdings.length)}`, 20, currentY);
    currentY += 6;
    doc.text(`Confirmed Holdings: ${formatNumber(holdings.filter(h => h.isConfirmed).length)}`, 20, currentY);
    currentY += 6;
    doc.text(`Unconfirmed Holdings: ${formatNumber(holdings.filter(h => !h.isConfirmed).length)}`, 20, currentY);
    currentY += 15;
  }

  // Exception Summary Section (below NAV)
  if (reportData.exceptionSummary) {
    addNewPageIfNeeded(40);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Exception Summary', 20, currentY);
    currentY += 10;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    const s = reportData.exceptionSummary;
    const summaryLines = [
      `Rules run: ${s.rulesRun}`,
      `Passes: ${s.passes}`,
      `Fails: ${s.failures}`,
      `Open failures: ${s.failuresOpen}`,
      `Resolved failures: ${s.failuresResolved}`,
    ];
    summaryLines.forEach(line => { doc.text(line, 20, currentY); currentY += 6; });
    currentY += 6;
  }

  // Exception Details Table
  if (reportData.exceptionDetails && reportData.exceptionDetails.length > 0) {
    addNewPageIfNeeded(60);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Exception Details', 20, currentY);
    currentY += 10;

    const detailRows = reportData.exceptionDetails.map(d => [
      d.rule || '',
      d.description || '',
      (d.result ?? '').toString(),
      (d.expected ?? '').toString(),
      (d.tolerance ?? '').toString(),
      `${d.assignedTo || ''}${d.checkedBy ? ' / ' + d.checkedBy : ''}`,
      d.notes || '',
      d.status || '',
    ]);

    autoTable(doc, {
      startY: currentY,
      head: [[ 'Rule', 'Description', 'Result', 'Expected', 'Tolerance', 'Assigned/Checked By', 'Notes', 'Status' ]],
      body: detailRows,
      theme: 'grid',
      styles: { fontSize: 9 },
      headStyles: { fillColor: [66, 139, 202] },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { left: 20, right: 20 },
      columnStyles: {
        1: { cellWidth: 60 },
        6: { cellWidth: 40 },
      },
    });
    currentY = (doc as any).lastAutoTable.finalY + 15;
  }

  // Holdings Detail Section
  if (reportOptions.includeHoldings) {
    addNewPageIfNeeded(80);
    
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`Holdings Detail (${holdings.length} positions)`, 20, currentY);
    currentY += 15;

    if (holdings.length > 0) {
      const holdingsTableData = holdings.map(holding => [
        holding.instrument.instrumentKey,
        holding.instrument.name,
        formatNumber(holding.quantity),
        formatCurrency(holding.baseAmount, holding.currency),
        holding.currency,
        holding.isConfirmed ? 'Confirmed' : 'Unconfirmed',
        holding.account.accountKey,
      ]);

      autoTable(doc, {
        startY: currentY,
        head: [['Symbol', 'Instrument Name', 'Quantity', 'Base Amount', 'Currency', 'Status', 'Account']],
        body: holdingsTableData,
        theme: 'grid',
        styles: { fontSize: 9 },
        headStyles: { fillColor: [66, 139, 202] },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { left: 20, right: 20 },
      });

      currentY = (doc as any).lastAutoTable.finalY + 15;
    } else {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.text('No holdings data available for this reporting period.', 20, currentY);
      currentY += 15;
    }
  }

  // Transaction Summary Section
  addNewPageIfNeeded(80);
  
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(`Transaction Summary (${transactions.length} transactions)`, 20, currentY);
  currentY += 15;

  if (transactions.length > 0) {
    // Transaction summary by currency
    const transactionSummary = transactions.reduce((summary, transaction) => {
      const currency = transaction.currency || 'USD';
      if (!summary[currency]) {
        summary[currency] = { total: 0, count: 0 };
      }
      summary[currency].total += transaction.baseAmount;
      summary[currency].count += 1;
      return summary;
    }, {} as Record<string, { total: number; count: number }>);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);

    Object.entries(transactionSummary).forEach(([currency, data]) => {
      doc.text(`${currency}: ${formatCurrency(data.total, currency)} (${data.count} transactions)`, 20, currentY);
      currentY += 6;
    });

    currentY += 10;

    // Recent transactions table (last 20)
    const recentTransactions = transactions
      .sort((a, b) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime())
      .slice(0, 20);

    if (recentTransactions.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.text('Recent Transactions (Last 20)', 20, currentY);
      currentY += 10;

      const transactionTableData = recentTransactions.map(transaction => [
        formatDate(transaction.transactionDate),
        transaction.transactionType,
        transaction.instrument.instrumentKey,
        formatNumber(transaction.quantity),
        formatCurrency(transaction.baseAmount, transaction.currency),
        transaction.currency,
        transaction.isConfirmed ? 'Confirmed' : 'Unconfirmed',
      ]);

      autoTable(doc, {
        startY: currentY,
        head: [['Date', 'Type', 'Symbol', 'Quantity', 'Amount', 'Currency', 'Status']],
        body: transactionTableData,
        theme: 'grid',
        styles: { fontSize: 9 },
        headStyles: { fillColor: [66, 139, 202] },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { left: 20, right: 20 },
      });

      currentY = (doc as any).lastAutoTable.finalY + 15;
    }
  } else {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.text('No transaction data available for this reporting period.', 20, currentY);
    currentY += 15;
  }

  // Sign-off Section at end
  addNewPageIfNeeded(60);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Sign-off', 20, currentY);
  currentY += 10;

  const pageWidth = doc.internal.pageSize.getWidth();
  const contentLeft = 20;
  const contentRight = pageWidth - 20;
  const labelGap = 25;
  const lineWidth = 60;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);

  // Run by
  doc.text('Run by:', contentLeft, currentY);
  doc.line(contentLeft + labelGap, currentY, contentLeft + labelGap + lineWidth, currentY);
  doc.text('Date:', contentLeft + labelGap + lineWidth + 20, currentY);
  doc.line(contentLeft + labelGap + lineWidth + 35, currentY, contentLeft + labelGap + lineWidth + 35 + 35, currentY);
  currentY += 12;

  // Checked by
  doc.text('Checked by:', contentLeft, currentY);
  doc.line(contentLeft + labelGap, currentY, contentLeft + labelGap + lineWidth, currentY);
  doc.text('Date:', contentLeft + labelGap + lineWidth + 20, currentY);
  doc.line(contentLeft + labelGap + lineWidth + 35, currentY, contentLeft + labelGap + lineWidth + 35 + 35, currentY);
  currentY += 18;

  // Approver with signature box
  doc.text('Approver (e-signature):', contentLeft, currentY);
  currentY += 6;
  const sigBoxHeight = 28;
  const sigBoxWidth = contentRight - contentLeft;
  doc.rect(contentLeft, currentY, sigBoxWidth, sigBoxHeight);
  currentY += sigBoxHeight + 8;
  doc.text('Name:', contentLeft, currentY);
  doc.line(contentLeft + 18, currentY, contentLeft + 18 + 80, currentY);
  doc.text('Date:', contentLeft + 120, currentY);
  doc.line(contentLeft + 138, currentY, contentLeft + 138 + 50, currentY);

  // Footer with page numbers and filename-like header/footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const footerY = pageHeight - 10;
    doc.text(`Page ${i} of ${pageCount} | ${new Date().toLocaleString()}`, 20, footerY);
    const headerRight = `${reportData.fundKey} - Oversight Summary Report`;
    doc.text(headerRight, pageWidth - 20, 10, { align: 'right' });
    doc.text('WealthBee Enterprise', pageWidth - 20, footerY, { align: 'right' });
  }

  // Convert to blob
  const pdfOutput = doc.output('blob');
  return pdfOutput;
}; 